import { useGoalById, useGoals } from 'features/goals';
import { useLocalizedLifeAreas } from 'features/life-areas';
import React, { useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';
import { Link, useNavigate } from 'react-router-dom';
import { BreadcrumbDivider, IconFormIcons, Target } from 'shared/assets/icons';
import { CircleProgress } from 'shared/components/ui/circle-progress';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenu,
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
  PopupMenuTitle,
} from 'shared/components/ui/popup-menu';
import { useTaskCounts } from 'shared/contexts/task-counts/use-task-counts';
import { useEntityLinks } from 'shared/hooks/use-entity-links';

import {
  getGoalBreadcrumbParents,
  GoalBreadcrumbItem,
} from './get-goal-breadcrumb-parents';
import * as Styled from './goal-detail-breadcrumbs.style';

type CrumbIconProps = {
  item: GoalBreadcrumbItem;
};

const CrumbIcon: React.FC<CrumbIconProps> = ({ item }) => (
  <Styled.IconWrapper>
    {item.hasProgress ? (
      <CircleProgress percentage={item.percentage} hidePercentage isSmall />
    ) : (
      <Icon
        icon={item.iconName ? IconFormIcons[item.iconName] ?? Target : Target}
      />
    )}
  </Styled.IconWrapper>
);

type ParentMenuItemProps = {
  item: GoalBreadcrumbItem;
  onNavigate: (path: string) => void;
};

const ParentMenuItem: React.FC<ParentMenuItemProps> = ({
  item,
  onNavigate,
}) => {
  const handleClick = () => onNavigate(item.path);

  return (
    <PopupMenuListItem>
      <PopupMenuButton
        overflowEllipsis
        start={<CrumbIcon item={item} />}
        onClick={handleClick}
      >
        {item.name}
      </PopupMenuButton>
    </PopupMenuListItem>
  );
};

export const GoalDetailBreadcrumbs: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams();
  const goal = useGoalById(id);
  const goals = useGoals();
  const lifeAreas = useLocalizedLifeAreas();
  const links = useEntityLinks();
  const navigate = useNavigate();
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const taskCounts = useTaskCounts();

  const parents = useMemo(
    () =>
      goal
        ? getGoalBreadcrumbParents(goal, {
            goals,
            lifeAreas,
            links,
            goalProgressCounts: taskCounts?.byGoalProgress,
          })
        : [],
    [goal, goals, lifeAreas, links, taskCounts?.byGoalProgress],
  );

  if (!goal) {
    return null;
  }

  const [firstParent] = parents;
  const hasMultipleParents = parents.length > 1;
  const parentNames = parents.map(({ name }) => name).join(', ');

  const onNavigate = (path: string) => {
    setIsOpen(false);
    navigate(path);
  };

  const handleOpenMenu = () => setIsOpen(true);
  const handleCloseMenu = () => setIsOpen(false);

  return (
    <Styled.Container>
      {!!firstParent && (
        <>
          <Styled.Crumb>
            {hasMultipleParents ? (
              <button
                ref={triggerRef}
                type="button"
                title={parentNames}
                aria-haspopup="menu"
                aria-expanded={isOpen}
                onClick={handleOpenMenu}
              >
                <CrumbIcon item={firstParent} />
                <Styled.CrumbName>{parentNames}</Styled.CrumbName>
              </button>
            ) : (
              <Link to={firstParent.path}>
                <CrumbIcon item={firstParent} />
                <Styled.CrumbName>{firstParent.name}</Styled.CrumbName>
              </Link>
            )}
          </Styled.Crumb>
          <Styled.Separator>
            <BreadcrumbDivider />
          </Styled.Separator>
        </>
      )}

      <Styled.Current title={goal.name}>{goal.name}</Styled.Current>

      {isOpen && (
        <PopupMenu referenceElement={triggerRef} onOutside={handleCloseMenu}>
          <PopupMenuList>
            <PopupMenuTitle title={t('general.contributes-to')} isSubTitle />
            {parents.map((item) => (
              <ParentMenuItem
                key={item.id}
                item={item}
                onNavigate={onNavigate}
              />
            ))}
          </PopupMenuList>
        </PopupMenu>
      )}
    </Styled.Container>
  );
};
